import { useEffect, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { Icon } from '../ui/Icon';
import { fmtEur, fmtDate } from '../../lib/utils';
import { Helper } from '../ui/Helper';
import { useOverlayA11y } from '../../lib/overlay';
import { useModalStore } from '../../stores/modal.store';
import { QuoteStatus } from '@masamune/shared';

/**
 * Wizard per creare un nuovo preventivo.
 *
 * Step: cliente → progetto → titolo+intro → righe → date → T&C → riepilogo
 *
 * Differenza chiave dal wizard fatture: questo è un documento commerciale
 * non fiscale. Inserisce title, introMessage e termsAndConditions che il
 * cliente vedrà nella pagina di firma.
 */

const PRESETS: Array<{ label: string; line: Line }> = [
  { label: 'Consulenza (h)',     line: { description: 'Consulenza specialistica',     quantity: 1, unitPrice: 80,    vatRate: 22 } },
  { label: 'Sviluppo',           line: { description: 'Attività di sviluppo',         quantity: 1, unitPrice: 2500,  vatRate: 22 } },
  { label: 'Setup una tantum',   line: { description: 'Setup iniziale',               quantity: 1, unitPrice: 1500,  vatRate: 22 } },
  { label: 'Canone mensile',     line: { description: 'Canone mensile servizio',      quantity: 1, unitPrice: 250,   vatRate: 22 } },
  { label: 'Design / UI',        line: { description: 'Design UI / branding',         quantity: 1, unitPrice: 1800,  vatRate: 22 } },
  { label: 'Riga libera',        line: { description: '',                              quantity: 1, unitPrice: 0,     vatRate: 22 } },
];

const TC_TEMPLATES = [
  {
    label: 'Standard 50% acconto',
    text: `1. Pagamento: 50% all'accettazione, 50% alla consegna.
2. Tempistiche: l'eventuale slittamento sarà comunicato con preavviso ragionevole.
3. Modifiche fuori ambito: gestite come change-request con quotazione separata.
4. Validità: il presente preventivo è valido fino alla data indicata in alto.`,
  },
  {
    label: 'Standard 30/40/30',
    text: `1. Pagamento: 30% all'accettazione, 40% a metà progetto, 30% alla consegna.
2. Tempistiche: indicate in milestone separati.
3. Cancellazione: in caso di interruzione, gli importi versati non sono rimborsabili.
4. Riservatezza: le parti si impegnano alla massima riservatezza.`,
  },
  {
    label: 'Retainer mensile',
    text: `1. Modalità: ore/giorni mensili come da fee indicata.
2. Pagamento: anticipato mese per mese, addebito al primo del mese.
3. Disdetta: 30 giorni di preavviso scritto.
4. Ore non utilizzate: non cumulabili sul mese successivo.`,
  },
  { label: 'Nessuno (libero)', text: '' },
];

interface Line {
  description: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
}

interface ClientLite {
  id: string;
  legalName: string;
  shortName: string;
  vat: string | null;
  sdiCode: string | null;
}

interface ProjectLite {
  id: string;
  code: string;
  name: string;
  clientId: string;
}

interface Answers {
  clientId: string | null;
  projectId: string | null;
  title: string;
  introMessage: string;
  termsAndConditions: string;
  lines: Line[];
  issueDate: string;
  validUntil: string;
}

const STEPS = ['cliente', 'progetto', 'intro', 'righe', 'date', 'termini', 'riepilogo'] as const;
type StepKey = typeof STEPS[number];

function addDays(iso: string, n: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export function NewQuoteFlow({
  onClose,
  clientId: initialClientId,
  projectId: initialProjectId,
  initialLines,
  initialTitle,
}: {
  onClose: () => void;
  clientId?: string;
  projectId?: string;
  /** Righe pre-compilate (es. derivate dai ricavi del progetto appena creato) → zero re-input. */
  initialLines?: Line[];
  /** Titolo suggerito (es. "<Progetto> — Proposta commerciale"). */
  initialTitle?: string;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tfRef = useOverlayA11y();
  const closing = useModalStore((s) => s.closing);

  const hasSeededLines = !!(initialLines && initialLines.length);
  // Skippa cliente se preselezionato; skippa progetto se entrambi preselezionati;
  // se arriviamo già col cliente+progetto+righe (binario progetto→preventivo) saltiamo
  // dritti allo step "righe" (idx 3) per la sola revisione: zero re-input.
  const initialIdx = initialProjectId && initialClientId && hasSeededLines
    ? 3
    : initialProjectId && initialClientId ? 2 : initialClientId ? 1 : 0;
  const [idx, setIdx] = useState(initialIdx);

  const [answers, setAnswers] = useState<Answers>({
    clientId: initialClientId ?? null,
    projectId: initialProjectId ?? null,
    title: initialTitle ?? '',
    introMessage: '',
    termsAndConditions: '',
    lines: hasSeededLines ? initialLines!.map((l) => ({ ...l })) : [{ description: '', quantity: 1, unitPrice: 0, vatRate: 22 }],
    issueDate: new Date().toISOString().slice(0, 10),
    validUntil: addDays(new Date().toISOString().slice(0, 10), 30),
  });

  const step = STEPS[idx]!;
  const next = () => setIdx(Math.min(idx + 1, STEPS.length - 1));
  const prev = () => setIdx(Math.max(idx - 1, 0));

  const { data: clients } = useQuery({
    queryKey: ['clients-lite-quotes'],
    queryFn: () => api.get<{ items: ClientLite[] }>('/clients?pageSize=200'),
  });
  const { data: projects } = useQuery({
    queryKey: ['projects-lite-quotes', answers.clientId],
    queryFn: () => api.get<{ items: ProjectLite[] }>(`/projects?pageSize=100&clientId=${answers.clientId}`),
    enabled: !!answers.clientId,
  });

  const selectedClient = useMemo(
    () => clients?.items.find((c) => c.id === answers.clientId) ?? null,
    [clients, answers.clientId],
  );

  const subtotal = answers.lines.reduce((a, l) => a + l.quantity * l.unitPrice, 0);
  const vat = answers.lines.reduce((a, l) => a + (l.quantity * l.unitPrice * l.vatRate) / 100, 0);
  const total = subtotal + vat;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && step !== 'riepilogo' && step !== 'intro' && step !== 'termini') {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.preventDefault();
        canAdvance() && next();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idx, step, answers]); // eslint-disable-line react-hooks/exhaustive-deps

  function canAdvance(): boolean {
    switch (step) {
      case 'cliente': return !!answers.clientId;
      case 'progetto': return true;
      case 'intro': return true;
      case 'righe': return answers.lines.length > 0 && answers.lines.every((l) => l.description.trim() && l.quantity > 0);
      case 'date': return !!answers.issueDate;
      case 'termini': return true;
      case 'riepilogo': return true;
    }
  }

  const createDraft = useMutation({
    mutationFn: () => api.post<{ id: string }>('/quotes', {
      clientId: answers.clientId!,
      projectId: answers.projectId,
      title: answers.title.trim() || null,
      introMessage: answers.introMessage.trim() || null,
      termsAndConditions: answers.termsAndConditions.trim() || null,
      issueDate: answers.issueDate,
      validUntil: answers.validUntil || null,
      lines: answers.lines,
      status: QuoteStatus.BOZZA,
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      toast.ok('Preventivo creato', `Totale € ${fmtEur(total)}`);
      onClose();
      navigate(`/quotes/${res.id}`);
    },
    onError: (e) => toast.alert('Errore', e instanceof ApiError ? e.message : 'Riprova'),
  });

  const pct = ((idx + 1) / STEPS.length) * 100;

  return (
    <div className={`tf${closing ? ' tf--leaving' : ''}`} ref={tfRef} role="dialog" aria-modal="true">
      <div className="tf-topbar">
        <div className="flex gap-2.5 items-center">
          <div className="h-[22px] grid place-items-center bg-ink rounded-1 px-2"><img src="/logos/m-white.svg" alt="Masamune" className="h-[9px] w-auto block" /></div>
          <div>
            <div className="font-bold text-[13px] tracking-[0.14em] uppercase">Nuovo preventivo</div>
            <div className="mute text-[12px]">Wizard commerciale · <span className="t-kbd">Enter</span> per continuare</div>
          </div>
        </div>
        <div className="tf-progress"><span style={{ width: `${pct}%` }} /></div>
        <div className="tf-step-count">{String(idx + 1).padStart(2, '0')} / {String(STEPS.length).padStart(2, '0')}</div>
        <button className="tb-icon-btn" onClick={onClose}><Icon name="x" /></button>
      </div>

      <div className="tf-body">
        <div className="tf-frame tf-enter-anim" key={step}>
          <div className="tf-step-label">
            <span className="num">{String(idx + 1).padStart(2, '0')}</span>
            {step.charAt(0).toUpperCase() + step.slice(1)}
          </div>

          {step === 'cliente' && <ClientStep answers={answers} setAnswers={setAnswers} next={next} clients={clients?.items ?? []} />}
          {step === 'progetto' && <ProjectStep answers={answers} setAnswers={setAnswers} next={next} projects={projects?.items ?? []} />}
          {step === 'intro' && <IntroStep answers={answers} setAnswers={setAnswers} client={selectedClient} />}
          {step === 'righe' && <LinesStep answers={answers} setAnswers={setAnswers} subtotal={subtotal} vat={vat} total={total} />}
          {step === 'date' && <DateStep answers={answers} setAnswers={setAnswers} />}
          {step === 'termini' && <TermsStep answers={answers} setAnswers={setAnswers} />}
          {step === 'riepilogo' && (
            <SummaryStep
              answers={answers}
              client={selectedClient}
              project={projects?.items.find((p) => p.id === answers.projectId) ?? null}
              subtotal={subtotal}
              vat={vat}
              total={total}
              onSave={() => createDraft.mutate()}
              saving={createDraft.isPending}
            />
          )}
        </div>
      </div>

      <div className="tf-footer">
        <div className="tf-hint-keys">
          <span><span className="t-kbd">↵</span> Avanti</span>
          <span><span className="t-kbd">Esc</span> Chiudi</span>
        </div>
        <div className="flex gap-2 items-center">
          {step === 'righe' && <span className="mono text-[13px] mute hidden md:inline">€ {fmtEur(total)} totale</span>}
          <button className="btn" onClick={prev} disabled={idx === 0}><Icon name="arrowLeft" size={12} /> Indietro</button>
          {step !== 'riepilogo' && (
            <button className="btn btn-primary" onClick={next} disabled={!canAdvance()}>
              Avanti <Icon name="arrowRight" size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
function ClientStep({ answers, setAnswers, next, clients }: { answers: Answers; setAnswers: (a: Answers) => void; next: () => void; clients: ClientLite[] }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    if (!q.trim()) return clients.slice(0, 8);
    const needle = q.toLowerCase();
    return clients.filter((c) => c.legalName.toLowerCase().includes(needle) || c.shortName.toLowerCase().includes(needle) || (c.vat && c.vat.includes(needle))).slice(0, 12);
  }, [q, clients]);

  return (
    <>
      <h2 className="tf-question">A chi vuoi mandare la proposta?</h2>
      <div className="tf-hint">Cerca un cliente esistente. Il preventivo sarà visibile solo a chi ha il link che invieremo.</div>
      <div className="mb-3 relative">
        <Icon name="search" size={14} className="absolute left-3 top-3 mute" />
        <input className="inp pl-9" placeholder="Cerca per ragione sociale o P.IVA…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
      </div>
      <div className="tf-options">
        {filtered.map((c, i) => (
          <div key={c.id} className="tf-option" aria-selected={answers.clientId === c.id}
            onClick={() => { setAnswers({ ...answers, clientId: c.id }); setTimeout(next, 180); }}>
            <span className="tf-option-key">{String.fromCharCode(65 + i)}</span>
            <div className="tf-option-body">
              <span>{c.legalName}</span>
              <span className="tf-option-sub">{c.vat ?? 'P.IVA mancante'}</span>
            </div>
            <Icon name="arrowRight" size={14} />
          </div>
        ))}
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
function ProjectStep({ answers, setAnswers, next, projects }: { answers: Answers; setAnswers: (a: Answers) => void; next: () => void; projects: ProjectLite[] }) {
  return (
    <>
      <h2 className="tf-question">Riguarda un progetto specifico?</h2>
      <div className="tf-hint">Opzionale. Collegare il preventivo a un progetto aiuta a tracciarne marginalità e cronologia.</div>
      <div className="tf-options">
        <div className="tf-option" aria-selected={answers.projectId === null} onClick={() => { setAnswers({ ...answers, projectId: null }); setTimeout(next, 180); }}>
          <span className="tf-option-key">—</span>
          <div className="tf-option-body"><span>Nessun progetto</span><span className="tf-option-sub">Proposta libera, non collegata a un progetto attivo</span></div>
        </div>
        {projects.map((p) => (
          <div key={p.id} className="tf-option" aria-selected={answers.projectId === p.id} onClick={() => { setAnswers({ ...answers, projectId: p.id }); setTimeout(next, 180); }}>
            <span className="tf-option-key mono text-[12px]">{p.code}</span>
            <div className="tf-option-body"><span>{p.name}</span></div>
            <Icon name="arrowRight" size={14} />
          </div>
        ))}
        {projects.length === 0 && <div className="py-3 text-center mute text-[13px]">Nessun progetto per questo cliente.</div>}
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
function IntroStep({ answers, setAnswers, client }: { answers: Answers; setAnswers: (a: Answers) => void; client: ClientLite | null }) {
  return (
    <>
      <h2 className="tf-question">Come la presenti al cliente?</h2>
      <div className="tf-hint">Titolo e messaggio iniziale sono i primi elementi che il cliente vedrà nella pagina di firma.</div>
      <div className="space-y-4 max-w-[680px]">
        <div>
          <label className="field-label">
            Titolo
            <Helper text="Il titolo viene mostrato come intestazione del preventivo. Es: 'Sito istituzionale Aureliana — Proposta commerciale'." />
          </label>
          <input
            className="inp text-[16px] py-2.5"
            placeholder={`Es. ${client?.legalName ?? 'Aureliana'} — Sito istituzionale 2026`}
            value={answers.title}
            onChange={(e) => setAnswers({ ...answers, title: e.target.value })}
            autoFocus
          />
        </div>
        <div>
          <label className="field-label">
            Messaggio introduttivo
            <Helper text="Compare sopra le righe del preventivo. Usalo per ringraziare per l'incontro, riassumere obiettivi, o anticipare il valore della proposta." />
          </label>
          <textarea
            className="inp"
            rows={5}
            placeholder="Ciao Andrea, dopo il nostro incontro di ieri abbiamo preparato la proposta che riassume lo scope discusso…"
            value={answers.introMessage}
            onChange={(e) => setAnswers({ ...answers, introMessage: e.target.value })}
          />
        </div>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
function LinesStep({ answers, setAnswers, subtotal, vat, total }: { answers: Answers; setAnswers: (a: Answers) => void; subtotal: number; vat: number; total: number }) {
  const updateLine = (i: number, patch: Partial<Line>) =>
    setAnswers({ ...answers, lines: answers.lines.map((l, j) => (i === j ? { ...l, ...patch } : l)) });

  const addPreset = (preset: Line) =>
    setAnswers({ ...answers, lines: [...answers.lines.filter((l) => l.description.trim() || l.unitPrice > 0), { ...preset }] });

  const remove = (i: number) =>
    setAnswers({ ...answers, lines: answers.lines.filter((_, j) => j !== i) });

  return (
    <>
      <h2 className="tf-question">Cosa proponiamo?</h2>
      <div className="tf-hint">
        Aggiungi una o più voci. Il cliente le vedrà esattamente così.
        <Helper text="Ogni riga ha quantità, prezzo unitario e IVA. Il totale è calcolato in tempo reale. Le voci appaiono nel preventivo nello stesso ordine in cui le aggiungi." />
      </div>

      <div className="space-y-2.5 mb-4">
        {answers.lines.map((l, i) => (
          <div key={i} className="grid items-center gap-2 p-2.5 rounded-1 border border-line hover:bg-paper-2 transition" style={{ gridTemplateColumns: '1fr 70px 110px 70px 100px 30px' }}>
            <input className="inp" placeholder="Descrizione (es. Sviluppo sito istituzionale — frontend Next.js)"
              value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })}
              autoFocus={i === answers.lines.length - 1 && !l.description} />
            <input className="inp mono text-right" type="number" min={0} step="any" value={l.quantity} onChange={(e) => updateLine(i, { quantity: Number(e.target.value) })} title="Quantità" />
            <div className="relative">
              <span className="absolute left-2.5 inset-y-0 flex items-center text-mute text-[13px] pointer-events-none">€</span>
              <input className="inp mono text-right pl-7" type="number" min={0} step="any" value={l.unitPrice} onChange={(e) => updateLine(i, { unitPrice: Number(e.target.value) })} title="Prezzo unitario" />
            </div>
            <select className="inp mono text-right" value={l.vatRate} onChange={(e) => updateLine(i, { vatRate: Number(e.target.value) })} title="IVA">
              <option value={22}>22%</option><option value={10}>10%</option><option value={4}>4%</option><option value={0}>0%</option>
            </select>
            <div className="mono text-right font-semibold">€ {fmtEur(l.quantity * l.unitPrice * (1 + l.vatRate / 100))}</div>
            <button
              type="button"
              className="grid place-items-center w-7 h-7 rounded-1 border border-line text-mute-2 hover:text-alert hover:border-alert hover:bg-alert-bg transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-line disabled:hover:bg-transparent disabled:hover:text-mute-2"
              onClick={() => remove(i)}
              disabled={answers.lines.length === 1}
              title={answers.lines.length === 1 ? 'Deve esserci almeno una riga' : 'Elimina riga'}
              aria-label="Elimina riga"
            >
              <Icon name="x" size={12} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="t-micro">Preset rapidi:</span>
        {PRESETS.map((p, i) => (
          <button key={i} className="btn btn-sm btn-dashed" onClick={() => addPreset(p.line)}>
            <Icon name="plus" size={10} /> {p.label}
          </button>
        ))}
      </div>

      <div className="border-t border-line pt-4 mt-2">
        <div className="grid grid-cols-3 gap-3 max-w-[480px] ml-auto">
          <div className="text-right"><div className="t-micro">Imponibile</div><div className="mono">€ {fmtEur(subtotal)}</div></div>
          <div className="text-right"><div className="t-micro">IVA</div><div className="mono mute">€ {fmtEur(vat)}</div></div>
          <div className="text-right"><div className="t-micro">Totale</div><div className="mono text-[20px] font-bold">€ {fmtEur(total)}</div></div>
        </div>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
function DateStep({ answers, setAnswers }: { answers: Answers; setAnswers: (a: Answers) => void }) {
  return (
    <>
      <h2 className="tf-question">Quando viene emesso e fino a quando vale?</h2>
      <div className="tf-hint">La scadenza è la data oltre la quale il preventivo non è più impegnativo per la nostra parte.</div>
      <div className="grid grid-cols-2 gap-4 max-w-[520px]">
        <div>
          <label className="field-label">
            Data emissione
            <Helper text="Quando il preventivo è stato preparato. Default oggi." />
          </label>
          <input className="inp mono" type="date" value={answers.issueDate} onChange={(e) => setAnswers({ ...answers, issueDate: e.target.value })} />
        </div>
        <div>
          <label className="field-label">
            Valido fino a
            <Helper text="Il cliente deve accettare entro questa data. Default 30 giorni — se non firma in tempo, il preventivo passa a stato 'SCADUTO' automaticamente." />
          </label>
          <input className="inp mono" type="date" value={answers.validUntil} onChange={(e) => setAnswers({ ...answers, validUntil: e.target.value })} />
        </div>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
function TermsStep({ answers, setAnswers }: { answers: Answers; setAnswers: (a: Answers) => void }) {
  return (
    <>
      <h2 className="tf-question">Termini e condizioni</h2>
      <div className="tf-hint">
        Compaiono in fondo al preventivo. Includi modalità di pagamento, tempistiche, clausole.
        <Helper text="Sono parte integrante del contratto: il cliente firmando dichiara di averli letti e accettati. Più precisi sono, meno discussioni dopo." />
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        {TC_TEMPLATES.map((t, i) => (
          <button key={i} className="btn btn-sm btn-dashed" onClick={() => setAnswers({ ...answers, termsAndConditions: t.text })}>
            <Icon name="file" size={10} /> {t.label}
          </button>
        ))}
      </div>

      <textarea
        className="inp font-mono"
        rows={10}
        placeholder="Pagamento, tempistiche, modifiche fuori ambito, validità…"
        value={answers.termsAndConditions}
        onChange={(e) => setAnswers({ ...answers, termsAndConditions: e.target.value })}
      />
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
function SummaryStep({ answers, client, project, subtotal, vat, total, onSave, saving }: {
  answers: Answers; client: ClientLite | null; project: ProjectLite | null;
  subtotal: number; vat: number; total: number; onSave: () => void; saving: boolean;
}) {
  return (
    <>
      <h2 className="tf-question">Anteprima del preventivo</h2>
      <div className="tf-hint">Controlla che sia tutto a posto. Lo salvi come bozza — l'invio al cliente lo fai dalla scheda dettaglio.</div>

      <div className="card max-w-[720px]">
        <div className="card-bd">
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className="t-micro">Preventivo</div>
              <div className="text-[16px] font-semibold">{answers.title || `Proposta per ${client?.legalName ?? '—'}`}</div>
            </div>
            <div className="text-right">
              <div className="t-micro">Emissione</div>
              <div className="mono">{fmtDate(answers.issueDate)}</div>
              {answers.validUntil && (<>
                <div className="t-micro mt-1">Valido fino a</div>
                <div className="mono">{fmtDate(answers.validUntil)}</div>
              </>)}
            </div>
          </div>

          <div className="border-t border-line pt-3 mt-1">
            <SummaryRow label="Cliente" value={client?.legalName ?? '—'} />
            {client?.vat && <SummaryRow label="P.IVA" value={client.vat} mono />}
            {project && <SummaryRow label="Progetto" value={`${project.code} — ${project.name}`} />}
          </div>

          {answers.introMessage && (
            <div className="border-t border-line pt-3 mt-3 text-[13px] mute whitespace-pre-wrap">
              {answers.introMessage}
            </div>
          )}

          <div className="border-t border-line pt-3 mt-3">
            <div className="t-micro mb-2">Voci ({answers.lines.length})</div>
            <table className="w-full text-[13px]">
              <tbody>
                {answers.lines.map((l, i) => (
                  <tr key={i}>
                    <td className="py-1.5">{l.description}</td>
                    <td className="py-1.5 mono mute text-right" style={{ width: 70 }}>{l.quantity} ×</td>
                    <td className="py-1.5 mono text-right" style={{ width: 100 }}>€ {fmtEur(l.unitPrice)}</td>
                    <td className="py-1.5 mono mute text-right" style={{ width: 50 }}>{l.vatRate}%</td>
                    <td className="py-1.5 mono font-semibold text-right" style={{ width: 100 }}>€ {fmtEur(l.quantity * l.unitPrice * (1 + l.vatRate / 100))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border-t border-line pt-3 mt-3 grid grid-cols-3 gap-3">
            <div className="text-right"><div className="t-micro">Imponibile</div><div className="mono">€ {fmtEur(subtotal)}</div></div>
            <div className="text-right"><div className="t-micro">IVA</div><div className="mono mute">€ {fmtEur(vat)}</div></div>
            <div className="text-right"><div className="t-micro">Totale</div><div className="mono text-[20px] font-bold">€ {fmtEur(total)}</div></div>
          </div>

          {answers.termsAndConditions && (
            <div className="border-t border-line pt-3 mt-3 text-[13px] mute font-mono whitespace-pre-wrap" style={{ maxHeight: 160, overflowY: 'auto' }}>
              {answers.termsAndConditions}
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button className="btn btn-primary" onClick={onSave} disabled={saving || !client}>
          <Icon name="check" size={12} /> {saving ? 'Creazione…' : 'Salva preventivo'}
        </button>
      </div>
      <div className="mt-2 mute text-[13px]">
        Dopo il salvataggio potrai invitarlo a firmare con un click dalla scheda dettaglio.
      </div>
    </>
  );
}

function SummaryRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between py-1.5 text-[13px]">
      <span className="mute">{label}</span>
      <span className={mono ? 'mono' : 'font-semibold'}>{value}</span>
    </div>
  );
}
